import { apiFetchBlob } from './api'

type AttMeta = {
  content_id?: string | null
  content_type?: string
  saved?: boolean
  filename?: string
}

/**
 * Resolve cid: and optionally remote images via authenticated fetch → blob: URLs.
 * Uses apiFetchBlob (401 → cookie refresh) so first open after idle JWT expiry still loads images.
 * Parallel fetch — marketing mail often has dozens of remote assets.
 */
export async function hydrateMailImages(
  root: HTMLElement,
  opts: {
    messageId: string
    attachments?: AttMeta[] | null
    loadRemote: boolean
    signal?: AbortSignal
  },
): Promise<{ remoteLoaded: number; cidLoaded: number }> {
  let remoteLoaded = 0
  let cidLoaded = 0

  const cidNodes = Array.from(root.querySelectorAll<HTMLElement>('[data-mail-cid]'))
  const atts = opts.attachments || []
  await Promise.all(
    cidNodes.map(async (el) => {
      if (opts.signal?.aborted) return
      const cid = (el.getAttribute('data-mail-cid') || '').trim().toLowerCase()
      if (!cid) return
      const idx = atts.findIndex(
        (a) => (a.content_id || '').replace(/^<|>$/g, '').toLowerCase() === cid,
      )
      if (idx < 0 || !atts[idx]?.saved) return
      try {
        const r = await apiFetchBlob(`/messages/${opts.messageId}/attachments/${idx}`, {
          signal: opts.signal,
        })
        if (!r.ok) return
        const blob = await r.blob()
        const url = URL.createObjectURL(blob)
        if (el.tagName === 'IMG') {
          ;(el as HTMLImageElement).src = url
        } else if (el.tagName === 'TD' || el.tagName === 'TH' || el.tagName === 'DIV' || el.tagName === 'SPAN' || el.tagName === 'A') {
          // Parent CSP blocks style= / CSSOM. Prefer a real <img> child over background-image.
          const img = el.ownerDocument.createElement('img')
          img.src = url
          img.alt = el.getAttribute('alt') || ''
          img.setAttribute('data-mail-bg-fallback', '1')
          el.insertBefore(img, el.firstChild)
        }
        el.removeAttribute('data-mail-cid')
        cidLoaded += 1
      } catch {
        /* abort / network — ignore */
      }
    }),
  )

  if (!opts.loadRemote) return { remoteLoaded, cidLoaded }

  const remoteNodes = Array.from(root.querySelectorAll<HTMLElement>('[data-mail-remote]'))
  await Promise.all(
    remoteNodes.map(async (el) => {
      if (opts.signal?.aborted) return
      const remote = el.getAttribute('data-mail-remote') || ''
      if (!/^https?:\/\//i.test(remote)) return
      try {
        const q = new URLSearchParams({ url: remote })
        const r = await apiFetchBlob(`/messages/${opts.messageId}/external-image?${q}`, {
          signal: opts.signal,
          accept: 'image/*,*/*',
        })
        if (!r.ok) return
        const blob = await r.blob()
        if (!blob.type.startsWith('image/') && blob.type !== 'application/octet-stream') return
        const url = URL.createObjectURL(blob)
        if (el.tagName === 'IMG') {
          ;(el as HTMLImageElement).src = url
        } else if (el.tagName === 'TD' || el.tagName === 'TH' || el.tagName === 'DIV' || el.tagName === 'SPAN' || el.tagName === 'A') {
          const img = el.ownerDocument.createElement('img')
          img.src = url
          img.alt = el.getAttribute('alt') || ''
          img.setAttribute('data-mail-bg-fallback', '1')
          el.insertBefore(img, el.firstChild)
        }
        el.removeAttribute('data-mail-remote')
        el.removeAttribute('data-mail-remotes')
        remoteLoaded += 1
      } catch {
        /* abort / network — ignore */
      }
    }),
  )

  return { remoteLoaded, cidLoaded }
}
